import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundaryWrapper } from "@/components/ErrorBoundary";
import { GlobalOfflineIndicator, OfflineProvider } from "@/components/OfflineProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { PosthogPageviewTracker } from "@/components/PosthogProvider";
import ChecklistManagement from "./pages/ChecklistManagement";
import CustomerDetails from "./pages/CustomerDetails";
import Customers from "./pages/Customers";
import DashboardPage from "./pages/DashboardPage";
import EditProject from "./pages/EditProject";
import Login from "./pages/Login";
import { MeetingsCalendar } from "./pages/MeetingsCalendar";
import NewProject from "./pages/NewProject";
import NotFound from "./pages/NotFound";
import ProjectDetailsPage from "./pages/ProjectDetails";
import Projects from "./pages/Projects";
import QuickReview from "./pages/QuickReview";
import QuoteBuilder from "./pages/QuoteBuilder";
import Settings from "./pages/Settings";
import TemplateManagement from "./pages/TemplateManagement";
import TestCustomers from "./pages/TestCustomers";
import UserManagement from "./pages/UserManagement";
import ViewAnalytics from "./pages/ViewAnalytics";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      import("@/lib/posthog").then(({ trackEvent }) => {
        trackEvent("api_error", {
          type: "query",
          queryKey: JSON.stringify(query.queryKey),
          error: error.message,
        });
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      import("@/lib/posthog").then(({ trackEvent }) => {
        trackEvent("api_error", {
          type: "mutation",
          mutationKey: mutation.options.mutationKey
            ? JSON.stringify(mutation.options.mutationKey)
            : undefined,
          error: error.message,
        });
      });
    },
  }),
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

const App = () => (
  <ErrorBoundaryWrapper>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <OfflineProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <GlobalOfflineIndicator />
            <ErrorBoundaryWrapper>
              <BrowserRouter>
                <PosthogPageviewTracker />
                <Routes>
                  {/* Public route */}
                  <Route path="/login" element={<Login />} />

                  {/* Protected routes */}
                  <Route path="/" element={<Navigate to="/dashboard" replace={true} />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/new-project"
                    element={
                      <ProtectedRoute>
                        <NewProject />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/edit-project/:projectId"
                    element={
                      <ProtectedRoute>
                        <EditProject />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/view-analytics"
                    element={
                      <ProtectedRoute>
                        <ViewAnalytics />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/customers"
                    element={
                      <ProtectedRoute>
                        <Customers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/customers/:id"
                    element={
                      <ProtectedRoute>
                        <CustomerDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/test-customers"
                    element={
                      <ProtectedRoute>
                        <TestCustomers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects"
                    element={
                      <ProtectedRoute>
                        <Projects />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects/:id"
                    element={
                      <ProtectedRoute>
                        <ProjectDetailsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/quote-builder/:projectId"
                    element={
                      <ProtectedRoute>
                        <QuoteBuilder />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin-only routes */}
                  <Route
                    path="/checklist-management"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <ChecklistManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/user-management"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <UserManagement />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/templates"
                    element={
                      <ProtectedRoute>
                        <TemplateManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/quick-review"
                    element={
                      <ProtectedRoute>
                        <QuickReview />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/meetings/calendar"
                    element={
                      <ProtectedRoute>
                        <MeetingsCalendar />
                      </ProtectedRoute>
                    }
                  />

                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </ErrorBoundaryWrapper>
          </TooltipProvider>
        </OfflineProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundaryWrapper>
);

export default App;
